<template>
  <Card class="organization-card">
    <template #content>
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-4">
          <div class="organization-avatar">
            <Avatar
              :label="organizationInitials"
              size="large"
              :style="{ backgroundColor: avatarColor }"
            />
          </div>
          <div class="organization-info">
            <h3 class="text-lg font-semibold">{{ organization.name }}</h3>
            <p class="text-sm text-gray-600">
              {{ organization.email || organization.phone || 'No contact info' }}
            </p>
            <div class="flex items-center gap-2 mt-1">
              <Tag
                v-if="organization.type"
                :value="formatOrganizationType(organization.type)"
                :severity="getOrganizationTypeSeverity(organization.type)"
                size="small"
              />
              <Tag
                v-if="organization.status"
                :value="formatStatus(organization.status)"
                :severity="getStatusSeverity(organization.status)"
                size="small"
              />
              <Tag
                v-if="organization.engagementStage"
                :value="formatEngagementStage(organization.engagementStage)"
                :severity="getEngagementStageSeverity(organization.engagementStage)"
                size="small"
              />
            </div>
            <div v-if="organization.city || organization.state" class="text-xs text-gray-500 mt-1">
              {{ [organization.city, organization.state].filter(Boolean).join(', ') }}
            </div>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Button
            icon="pi pi-eye"
            size="small"
            text
            @click="$emit('view', organization)"
            v-tooltip="'View Details'"
          />
          <Button
            icon="pi pi-pencil"
            size="small"
            text
            @click="$emit('edit', organization)"
            v-tooltip="'Edit'"
          />
          <Button
            icon="pi pi-trash"
            size="small"
            text
            severity="danger"
            @click="$emit('delete', organization)"
            v-tooltip="'Delete'"
          />
        </div>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type {
  Organization,
  OrganizationType,
  OrganizationStatus,
  EngagementStage,
} from '@/types/Organization'

// Components
import Avatar from 'primevue/avatar'
import Button from 'primevue/button'
import Card from 'primevue/card'
import Tag from 'primevue/tag'

interface Props {
  organization: Organization
}

const props = defineProps<Props>()

const emit = defineEmits<{
  view: [organization: Organization]
  edit: [organization: Organization]
  delete: [organization: Organization]
}>()

const organizationInitials = computed(() => {
  const words = props.organization.name.split(' ')
  if (words.length >= 2) {
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase()
  }
  return props.organization.name.substring(0, 2).toUpperCase()
})

const avatarColor = computed(() => {
  // Generate a consistent color based on the organization's name
  const name = props.organization.name
  const colors = [
    '#ef4444',
    '#f97316',
    '#f59e0b',
    '#eab308',
    '#84cc16',
    '#22c55e',
    '#10b981',
    '#14b8a6',
    '#06b6d4',
    '#0ea5e9',
    '#3b82f6',
    '#6366f1',
    '#8b5cf6',
    '#a855f7',
    '#d946ef',
    '#ec4899',
    '#f43f5e',
  ]
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length]
})

function formatOrganizationType(type: OrganizationType): string {
  switch (type) {
    case 'customer':
      return 'Customer'
    case 'supplier':
      return 'Supplier'
    case 'both':
      return 'Both'
    case 'other':
      return 'Other'
    default:
      return type
  }
}

function getOrganizationTypeSeverity(
  type: OrganizationType,
): 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast' {
  switch (type) {
    case 'customer':
      return 'info'
    case 'supplier':
      return 'success'
    case 'both':
      return 'warning'
    case 'other':
      return 'secondary'
    default:
      return 'secondary'
  }
}

function formatStatus(status: OrganizationStatus): string {
  switch (status) {
    case 'active':
      return 'Active'
    case 'inactive':
      return 'Inactive'
    case 'lead':
      return 'Lead'
    case 'prospect':
      return 'Prospect'
    case 'blacklisted':
      return 'Blacklisted'
    case 'former':
      return 'Former'
    case 'archived':
      return 'Archived'
    default:
      return status
  }
}

function getStatusSeverity(
  status: OrganizationStatus,
): 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast' {
  switch (status) {
    case 'active':
      return 'success'
    case 'inactive':
      return 'secondary'
    case 'lead':
      return 'warning'
    case 'prospect':
      return 'info'
    case 'blacklisted':
      return 'danger'
    case 'former':
      return 'secondary'
    case 'archived':
      return 'secondary'
    default:
      return 'secondary'
  }
}

function formatEngagementStage(stage: EngagementStage): string {
  switch (stage) {
    case 'uncontacted':
      return 'Uncontacted'
    case 'contacted':
      return 'Contacted'
    case 'qualified':
      return 'Qualified'
    case 'active':
      return 'Active'
    case 'rejected':
      return 'Rejected'
    case 'former':
      return 'Former'
    default:
      return stage
  }
}

function getEngagementStageSeverity(
  stage: EngagementStage,
): 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast' {
  switch (stage) {
    case 'uncontacted':
      return 'secondary'
    case 'contacted':
      return 'info'
    case 'qualified':
      return 'warning'
    case 'active':
      return 'success'
    case 'rejected':
      return 'danger'
    case 'former':
      return 'secondary'
    default:
      return 'secondary'
  }
}
</script>

<style scoped>
.organization-card {
  transition: all 0.2s ease-in-out;
}

.organization-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.organization-avatar {
  flex-shrink: 0;
}

.organization-info {
  flex: 1;
  min-width: 0;
}
</style>
